import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, FormControl, InputLabel, Typography } from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import FoodBank from '@mui/icons-material/FoodBank';
import { randomId } from '@mui/x-data-grid-generator';
import CustomTable from '../tables/MuiDataGrid';
import { Create } from '@mui/icons-material';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | SKUS | Manage';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({ count: 0, skus: [] });

  const [searchInput, setSearchInput] = React.useState({
    display_name: 'All',
    is_active: 'All',
    sku_type_id: 'All',
    missing_in_database: 'All',
  });

  const [filterOptions, setFilterOptions] = React.useState({
    skuTypes: [],
    skus: [],
  });

  const [accountingDbs, setAccountingDbs] = React.useState([]);
  React.useEffect(() => {
    bulkAxios(['/sku-types/namesUsedSkus', '/skus/names'])
      .then(async (all) => {
        setFilterOptions((pr) => ({
          skuTypes: all[0]?.skuTypes,
          skus: all[1]?.skus,
        }));
      })
      .catch((err) => console.log(err));
  }, []);

  React.useEffect(() => {
    setDataIsFetching((current) => {
      if (!current) return true;
    });
    userRequest()
      .get(`/skus?per_page=${perPage}&page=${page}&${buildQuery()}`)
      .then(({ data }) => {
        setTableData({ count: data.total, skus: data.skus });
        setDataIsFetching(false);
      })
      .catch((response) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }, [perPage, page, searchInput]);

  function getDatabases() {
    userRequest()
      .get('/accounting-databases/')
      .then(({ data }) => {
        setAccountingDbs(data.accountingDatabases ?? []);
      });
  }
  React.useEffect(getDatabases, []);

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }

    return true;
  }

  function setStatus(id, data) {
    userRequest()
      .post(`/skus/update/${id}`, data)
      .then(() => {
        let skus = [];
        if (meetFilterConditions(data)) {
          skus = tableData.skus.map((s) => {
            if (s.id === id) s.is_active = !s.is_active;
            return s;
          });
        } else {
          skus = tableData.skus.filter((s) => s.id !== id);
        }
        setTableData((prev) => {
          return { count: skus.length, skus };
        });
      })
      .catch(() => {});
  }

  function buildQuery(foundFilter = null) {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (foundFilter) foundFilter.push(key);
          let tempKey = key;
          let tempValue = value;
          if (key === 'is_active') {
            if (value === 'ALL') {
              tempKey = '';
              tempValue = '';
            } else if (value === 'ACTIVE') {
              tempValue = 'true';
            } else if (value === 'INACTIVE') {
              tempValue = 'false';
            }
          }

          if (key === 'display_name') {
            tempKey = 'id';
            tempValue = Number(value);
            if (!tempValue) {
              tempKey = '';
              tempValue = '';
            }
          }
          // Check for "All" and treat it like an empty string
          if (tempValue === 'All') {
            tempKey = '';
            tempValue = '';
          }
          return encodeURIComponent(tempKey) + '=' + encodeURIComponent(tempValue);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }

  const onChangeInput = (e, val, name = null) => {
    if (name) setSearchInput({ ...searchInput, [name]: val });
    else setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
  };

  const onSearch = (e) => {
    e.preventDefault();
    const queryString = buildQuery();

    userRequest()
      .get(`/skus?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({ count: data.total, skus: data.skus });
      })
      .catch(({ response }) => {
        console.log(response);
        setTableData({ count: 0, skus: [] });
      });
  };

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'display_name') {
            if (value === 'All') {
              return [];
            } else {
              return `Display Name: ${value}`;
            }
          }
          if (key === 'sku_type_id') {
            if (value === 'All') {
              return [];
            } else {
              const skuType = filterOptions.skuTypes.find((x) => x.id == value);
              return skuType ? `SKU Type: ${skuType.display_name}` : null;
            }
          }
          if (key === 'is_active') {
            if (value === 'All') {
              return [];
            } else if (value === 'ACTIVE') {
              return `Status: Active`;
            } else if (value === 'INACTIVE') {
              return `Status: Inactive`;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  const actionMethods = {
    toggleActive: (row) => {
      setStatus(row.id, { is_active: !row.is_active });
      userRequest()
        .get(`/skus?per_page=${perPage}&page=${page}&${buildQuery()}`)
        .then(({ data }) => {
          setTableData({ count: data.total, skus: data.skus });
          setDataIsFetching(false);
        });
    },

    link: {
      toolTip: 'Edit',
      icon: <Create />,
      getLink: (row) => `/skus/edit/${row.id}`,
    },
  };

  function getAccountingDatagridColumns() {
    return accountingDbs.flatMap((db) => {
      return [
        {
          field: `${db}_accounting_code`,
          headerName: `${db} Code`,
          type: 'number',
          flex: 1,
          renderCell: ({ row }) => {
            if (row.accountingData?.[db]?.code) return row.accountingData?.[db]?.code;
            else return <Typography color={'red'}>Pending</Typography>;
          },
        },
      ];
    });
  }

  const columns = [
    {
      field: 'id',
      headerName: 'SKU ID',
      flex: 1,
    },
    {
      field: 'sku',
      headerName: 'SKU',
      flex: 1,
      valueGetter: ({ row }) => `${row.sku_type} ${row.quantity_in_unit_of_measure}${row.uom}`,
    },
    ...getAccountingDatagridColumns(),
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<FoodBank />} title="Manage" parent="SKUs" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Display Name</InputLabel>
                <Select
                  name="display_name"
                  value={searchInput.display_name}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Display Name"
                  style={{ fontStyle: searchInput.display_name === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.skus.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">SKU Type</InputLabel>
                <Select
                  name="sku_type_id"
                  value={searchInput.sku_type_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="SKU Type"
                  style={{ fontStyle: searchInput.sku_type_id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.skuTypes.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Accounting Code</InputLabel>
                <Select
                  name="missing_in_database"
                  value={searchInput.missing_in_database}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Accounting Code"
                  style={{ fontStyle: searchInput.sku_type_id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={'All Missing'}>Show missing in any database</MenuItem>
                  {accountingDbs.map((db, i) => (
                    <MenuItem key={i} value={db}>
                      Show missing in {db}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
                <Select
                  name="is_active"
                  value={searchInput.is_active}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Status"
                  style={{ fontStyle: searchInput.is_active === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          history.push('/skus/create');
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.skus}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          getRowId={() => randomId()}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'display_name', sort: 'asc' }]}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
